import { Stack, Tile } from "@assetwatch/ui";
import type { Dispatch, SetStateAction } from "react";
import {
  ProcedureHeadingBlock,
  ProcedureItemList,
  ProcedureSectionBlock,
  ProcedureStepsHeader,
} from "./ProcedureBuilderParts";
import { ProcedurePropertiesPanel } from "./ProcedurePropertiesPanel";
import type {
  duplicateGroup,
  duplicateSection,
  ProcedureAuthoringDraft,
  ProcedureItem,
} from "./procedureAuthoring";

type SelectedStructure = { kind: "heading" | "section"; id: string } | null;

type ProcedureBuilderContentProps = {
  collapsedGroups: Record<string, boolean>;
  draft: ProcedureAuthoringDraft;
  errors: Record<string, string>;
  nextId: () => string;
  selectedItem: ProcedureItem | null;
  selectedItemId: string | null;
  selectedStructure: SelectedStructure;
  setCollapsedGroups: Dispatch<SetStateAction<Record<string, boolean>>>;
  setDraft: Dispatch<SetStateAction<ProcedureAuthoringDraft>>;
  setDraggingItem: Dispatch<
    SetStateAction<{ sectionId: string | null; itemId: string } | null>
  >;
  setSelectedItemId: Dispatch<SetStateAction<string | null>>;
  setSelectedStructure: Dispatch<SetStateAction<SelectedStructure>>;
  addHeading: () => void;
  addChoice: (itemId: string) => void;
  addSectionToGroup: (groupId: string | null) => void;
  addNewItem: (groupId: string | null, sectionId: string | null) => void;
  addItemAfter: (
    groupId: string | null,
    sectionId: string | null,
    afterItemId: string,
  ) => void;
  duplicateItemAndFocus: (sectionId: string | null, itemId: string) => void;
  duplicateSection: typeof duplicateSection;
  duplicateGroup: typeof duplicateGroup;
  handleItemDragOver: (sectionId: string | null, targetItemId: string) => void;
  handleItemDrop: (sectionId: string | null, targetItemId?: string) => void;
  removeItem: (sectionId: string | null, itemId: string) => void;
  removeChoice: (itemId: string, optionIndex: number) => void;
  removeGroup: (groupId: string) => void;
  removeSection: (groupId: string | null, sectionId: string) => void;
};

export function ProcedureBuilderContent({
  collapsedGroups,
  draft,
  errors,
  nextId,
  selectedItem,
  selectedItemId,
  selectedStructure,
  setCollapsedGroups,
  setDraft,
  setDraggingItem,
  setSelectedItemId,
  setSelectedStructure,
  addHeading,
  addChoice,
  addSectionToGroup,
  addNewItem,
  addItemAfter,
  duplicateItemAndFocus,
  duplicateSection: _duplicateSection,
  duplicateGroup: _duplicateGroup,
  handleItemDragOver,
  handleItemDrop,
  removeItem,
  removeChoice,
  removeGroup,
  removeSection,
}: ProcedureBuilderContentProps) {
  const sectionOptions = [
    { value: "__unsectioned__", label: "Unsectioned items" },
    ...draft.looseSections.map((section, index) => ({
      value: section.id,
      label: `Section ${index + 1}: ${section.title || "Untitled section"}`,
    })),
    ...draft.groups.flatMap((group, groupIndex) =>
      group.sections.map((section, sectionIndex) => ({
        value: section.id,
        label: `Heading ${groupIndex + 1}, section ${sectionIndex + 1}: ${section.title || "Untitled section"}`,
      })),
    ),
  ];
  return (
    <div className="grid min-h-0 gap-(--spacing-16) lg:grid-cols-[minmax(0,1fr)_340px]">
      <Stack gap={16}>
        <Tile hasBorder padding="none" width="full">
          <ProcedureStepsHeader
            addHeading={addHeading}
            addSectionToGroup={addSectionToGroup}
            draft={draft}
          />
          <div className="p-(--spacing-16)">
            <Stack gap={16}>
              {draft.looseSections.length ? (
                <div className="flex items-center gap-(--spacing-8) text-(length:--font-size-label-small) uppercase tracking-wider text-text-subtle">
                  <span>Sections</span>
                  <div className="h-px flex-1 bg-outline-subtle" />
                </div>
              ) : null}
              {draft.looseSections.map((section, sectionIndex) => (
                <ProcedureSectionBlock
                  addItemAfter={addItemAfter}
                  addNewItem={addNewItem}
                  duplicateItemAndFocus={duplicateItemAndFocus}
                  errors={errors}
                  groupId={null}
                  handleItemDragOver={handleItemDragOver}
                  handleItemDrop={handleItemDrop}
                  key={section.id}
                  nextId={nextId}
                  removeItem={removeItem}
                  removeSection={removeSection}
                  section={section}
                  sectionOptions={sectionOptions}
                  sectionCount={draft.looseSections.length}
                  sectionIndex={sectionIndex}
                  selectedItemId={selectedItemId}
                  selectedStructure={selectedStructure}
                  setDraft={setDraft}
                  setDraggingItem={setDraggingItem}
                  setSelectedItemId={setSelectedItemId}
                  setSelectedStructure={setSelectedStructure}
                />
              ))}
              {draft.groups.length ? (
                <div className="flex items-center gap-(--spacing-8) text-(length:--font-size-label-small) uppercase tracking-wider text-text-subtle">
                  <span>Headings</span>
                  <div className="h-px flex-1 bg-outline-subtle" />
                </div>
              ) : null}
              {draft.groups.map((group, groupIndex) => (
                <ProcedureHeadingBlock
                  addItemAfter={addItemAfter}
                  addNewItem={addNewItem}
                  addSectionToGroup={addSectionToGroup}
                  collapsed={Boolean(collapsedGroups[group.id])}
                  duplicateItemAndFocus={duplicateItemAndFocus}
                  errors={errors}
                  group={group}
                  groupCount={draft.groups.length}
                  groupIndex={groupIndex}
                  handleItemDragOver={handleItemDragOver}
                  handleItemDrop={handleItemDrop}
                  key={group.id}
                  nextId={nextId}
                  removeGroup={removeGroup}
                  removeItem={removeItem}
                  removeSection={removeSection}
                  sectionOptions={sectionOptions}
                  selectedItemId={selectedItemId}
                  selectedStructure={selectedStructure}
                  setCollapsedGroups={setCollapsedGroups}
                  setDraft={setDraft}
                  setDraggingItem={setDraggingItem}
                  setSelectedItemId={setSelectedItemId}
                  setSelectedStructure={setSelectedStructure}
                />
              ))}
              <ProcedureItemList
                addItemAfter={addItemAfter}
                addNewItem={addNewItem}
                duplicateItemAndFocus={duplicateItemAndFocus}
                errors={errors}
                groupId={null}
                handleItemDragOver={handleItemDragOver}
                handleItemDrop={handleItemDrop}
                items={draft.looseItems}
                removeItem={removeItem}
                sectionId={null}
                sectionOptions={sectionOptions}
                selectedItemId={selectedItemId}
                setDraft={setDraft}
                setDraggingItem={setDraggingItem}
                setSelectedItemId={setSelectedItemId}
              />
            </Stack>
          </div>
        </Tile>
      </Stack>
      <ProcedurePropertiesPanel
        addChoice={addChoice}
        draft={draft}
        errors={errors}
        removeChoice={removeChoice}
        selectedItem={selectedItem}
        selectedStructure={selectedStructure}
        setDraft={setDraft}
      />
    </div>
  );
}
